import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';
import { SlotState } from '@/features/editor/widgets/custom-widget/loading-section/shared/models';
import {
  LoadingStrategyOption,
  RecommendationType,
} from '@/webapi/use-widget-catalog-api';
import { ProductTile } from '@/features/editor/widgets/custom-widget/loading-section/shared/product-tile';
import { StrategyTile } from '@/features/editor/widgets/custom-widget/loading-section/shared/strategy-tile';
import { EmptySlot } from '@/features/editor/widgets/custom-widget/loading-section/shared/empty-slot';

type Props = {
  slot: SlotState;
  onProductSelect: () => void;
  onStrategySelect: (value: LoadingStrategyOption) => () => void;
  onDuplicateSelect: (value: number) => () => void;
  onDelete: () => void;
  onMoreFilters: () => void;
  index?: number;
  slots?: SlotState[];
};

export function Slot({
  slot,
  onMoreFilters,
  onStrategySelect,
  onProductSelect,
  onDuplicateSelect,
  onDelete,
  index,
  slots,
}: Props) {
  const filterLen = slot?.filter?.length || 0;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slot.num });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isProductTile = !!slot.manualProduct;
  const isStrategyTile =
    !isProductTile &&
    slot.strategy &&
    slot.strategy !== RecommendationType.MANUAL;
  const isEmptyTile = !isProductTile && !isStrategyTile;

  return (
    <DraggingWrapper
      ref={setNodeRef}
      className={`dragging-slot ${isDragging && `dragging`}`}
      style={style}
    >
      {isProductTile && (
        <ProductTile
          draggableAttributes={attributes}
          draggableListeners={listeners}
          p={slot.manualProduct}
          slotNum={slot.num}
          filterLen={filterLen}
          onDelete={onDelete}
          onMoreFilters={onMoreFilters}
          index={index}
        />
      )}
      {isStrategyTile && (
        <StrategyTile
          draggableAttributes={attributes}
          draggableListeners={listeners}
          strategy={slot.strategy}
          slotNum={slot.num}
          onDelete={onDelete}
          onMoreFilters={onMoreFilters}
          filterLen={filterLen}
          index={index}
        />
      )}
      {isEmptyTile && (
        <EmptySlot
          draggableAttributes={attributes}
          draggableListeners={listeners}
          slotNum={slot.num}
          onProductSelect={onProductSelect}
          onStrategySelect={onStrategySelect}
          onDuplicateSelect={onDuplicateSelect}
          index={index}
          slots={slots}
        />
      )}
    </DraggingWrapper>
  );
}

const DraggingWrapper = styled.div`
  border-radius: 8px;

  && > div {
    height: 100%;
  }

  &.dragging {
    cursor: grabbing;
    z-index: 999999999;
    box-shadow: rgba(0, 0, 0, 0.15) 0 54px 55px,
      rgba(0, 0, 0, 0.06) 0 -12px 30px, rgba(0, 0, 0, 0.06) 0 4px 6px,
      rgba(0, 0, 0, 0.09) 0 12px 13px, rgba(0, 0, 0, 0.04) 0 -3px 5px;
  }
`;
